// ─── ANALYTICS SCREEN ─────────────────────────────────────────
const AnalyticsScreen = ({ products = [] }) => {
  const [range, setRange]           = useState('all');
  const [data, setData]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [productFilterOpen, setProductFilterOpen] = useState(false);
  const [shippingMapOpen, setShippingMapOpen] = useState(false);
  const [shippingMapData, setShippingMapData] = useState(null);
  const [shippingMapReady, setShippingMapReady] = useState(false);
  const [shippingMapError, setShippingMapError] = useState('');
  const [shippingMapDisplaySummary, setShippingMapDisplaySummary] = useState({ orders:0, locations:0, level:'exact' });
  const [chartReady, setChartReady] = useState(() => !!window.Chart);
  const [chartError, setChartError] = useState('');
  const [timelineMetric, setTimelineMetric] = useState('revenue');
  const [sourceMetric, setSourceMetric] = useState('revenue');
  const [countryMetric, setCountryMetric] = useState('orders');

  const timelineRef = useRef(null);
  const sourceRef   = useRef(null);
  const shippingMapRef = useRef(null);
  const shippingMapInstance = useRef(null);
  const shippingMapCircles = useRef([]);
  const shippingMapZoomListener = useRef(null);
  const shippingMapClickListener = useRef(null);
  const shippingMapPopupOverlay = useRef(null);
  const shippingMapFittedFilter = useRef('');
  const charts      = useRef({});
  const productFilterRef = useRef(null);

  useEffect(() => {
    let active = true;
    if (window.Chart) {
      setChartReady(true);
      return () => { active = false; };
    }
    loadChartJs()
      .then(() => { if (active) setChartReady(true); })
      .catch(() => { if (active) setChartError('Charts could not be loaded.'); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setLoading(true);
    setData(null);
    const qs = new URLSearchParams();
    qs.set('range', range);
    if (selectedProductIds.length) qs.set('product_ids', selectedProductIds.join(','));
    api('GET', `/api/analytics?${qs}`).then(({ ok, data: d }) => {
      if (ok) setData(d);
      setLoading(false);
    });
  }, [range, selectedProductIds]);

  useEffect(() => {
    const valid = new Set(products.map(p => p.id));
    setSelectedProductIds(ids => ids.filter(id => valid.has(id)));
  }, [products]);

  const handleRangeClick = (v) => {
    setRange(v);
  };
  const toggleProduct = id => {
    setSelectedProductIds(ids => ids.includes(id) ? ids.filter(v => v !== id) : [...ids, id]);
  };

  useEffect(() => {
    const onPointerDown = e => {
      if (productFilterRef.current && !productFilterRef.current.contains(e.target)) {
        setProductFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    const C = window.Chart;
    if (!data || !chartReady || !C) return;

    const destroy = () => {
      Object.values(charts.current).forEach(c => { try { c.destroy(); } catch {} });
      charts.current = {};
    };
    destroy();

    C.defaults.font.family = 'Inter, sans-serif';
    C.defaults.color = '#4B5563';

    const blue = '#006FDD';
    const metricLabel = metric => metric === 'revenue' ? 'Revenue' : 'Orders';
    const metricValue = (metric, row) => metric === 'revenue' ? Number(row.revenue || 0) : Number(row.count || 0);
    const formatMetric = (metric, value) => metric === 'revenue'
      ? fmtMoney(value || 0, data.kpis?.currency)
      : `${Number(value || 0).toLocaleString()} order${Number(value || 0) === 1 ? '' : 's'}`;

    const timelineRows = fillDailyAnalytics(data.ordersOverTime || [], range, null);

    if (timelineRef.current && timelineRows.length > 0) {
      const ctx  = timelineRef.current.getContext('2d');
      const grad = ctx.createLinearGradient(0, 0, 0, 220);
      grad.addColorStop(0, 'rgba(0,111,221,0.18)');
      grad.addColorStop(1, 'rgba(0,111,221,0.01)');
      charts.current.timeline = new C(ctx, {
        type: 'line',
        data: {
          labels: timelineRows.map(d => d.date),
          datasets: [{
            data: timelineRows.map(row => metricValue(timelineMetric, row)),
            borderColor: blue, backgroundColor: grad,
            borderWidth: 1.75, tension: 0.08, fill: true,
            borderCapStyle: 'butt', borderJoinStyle: 'miter',
            pointRadius: timelineRows.length > 30 ? 0 : 3,
            pointHoverRadius: 4, pointHitRadius: 18, pointBackgroundColor: '#fff',
            pointBorderColor: blue, pointBorderWidth: 1.5,
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false, devicePixelRatio: Math.max(window.devicePixelRatio || 1, 2),
          interaction: { mode: 'index', intersect: false, axis: 'x' },
          hover: { mode: 'index', intersect: false },
          elements: { line: { cubicInterpolationMode: 'default' } },
          plugins: {
            legend: { display: false },
            tooltip: { mode: 'index', intersect: false, callbacks: { title: i => i[0].label, label: i => ` ${metricLabel(timelineMetric)}: ${formatMetric(timelineMetric, i.raw)}` } },
          },
          scales: {
            x: { grid: { display: false }, border: { color: '#E5E7EB' }, ticks: { font: { size: 11, weight: 500 }, color: '#6B7280', maxTicksLimit: 8 } },
            y: {
              grid: { color: '#E5E7EB', lineWidth: 1 },
              border: { display: false },
              ticks: {
                font: { size: 11, weight: 500 },
                color: '#6B7280',
                precision: timelineMetric === 'revenue' ? undefined : 0,
                padding: 8,
                callback: value => timelineMetric === 'revenue' ? fmtMoney(value, data.kpis?.currency).replace(/\.00$/, '') : value,
              },
              beginAtZero: true,
            },
          },
        },
      });
    }

    const sourceRows = [...(data.bySource || [])].sort((a, b) => metricValue(sourceMetric, b) - metricValue(sourceMetric, a));
    const sourceTotal = sourceRows.reduce((sum, row) => sum + metricValue(sourceMetric, row), 0);
    if (sourceRef.current && sourceRows.length > 0 && sourceTotal > 0) {
      charts.current.source = new C(sourceRef.current, {
        type: 'doughnut',
        data: {
          labels: sourceRows.map(s => PLATFORM_CFG[s.source]?.label || s.source),
          datasets: [{
            data: sourceRows.map(row => metricValue(sourceMetric, row)),
            backgroundColor: sourceRows.map(s => PLATFORM_CFG[s.source]?.color || '#6B7280'),
            borderWidth: 1, borderColor: '#fff', hoverBorderColor: '#fff',
          }],
        },
        options: {
          responsive: true, maintainAspectRatio: false, cutout: '66%', devicePixelRatio: Math.max(window.devicePixelRatio || 1, 2),
          interaction: { mode: 'nearest', intersect: false },
          plugins: {
            legend: { position: 'bottom', labels: { usePointStyle: true, pointStyle: 'circle', font: { size: 12, weight: 500 }, color: '#4B5563', padding: 14 } },
            tooltip: { intersect: false, callbacks: { label: i => ` ${formatMetric(sourceMetric, i.raw)} (${sourceTotal ? Math.round(i.raw / sourceTotal * 100) : 0}%)` } },
          },
        },
      });
    }

    return destroy;
  }, [data, range, timelineMetric, sourceMetric, chartReady]);

  const shippingMapModeForZoom = zoom => {
    const z = Number(zoom || 2);
    if (z >= 10) return 'exact';
    if (z >= 6) return 'place';
    return 'wide';
  };
  const shippingMapCountryCode = value => (
    countryCodeFromValue(value) || String(value || '').trim().toUpperCase() || 'unknown'
  );
  const shippingMapUsStateCode = value => {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const normalized = typeof normalizeUsStateCode === 'function'
      ? normalizeUsStateCode(raw)
      : raw.replace(/\./g, '').replace(/\s+/g, ' ').toUpperCase();
    const validCodes = typeof ALL_US_STATE_CODES !== 'undefined' ? ALL_US_STATE_CODES : [];
    return validCodes.includes(normalized) ? normalized : '';
  };
  const shippingMapUsStateName = code => {
    const rows = typeof US_STATE_OPTIONS !== 'undefined' ? US_STATE_OPTIONS : [];
    return rows.find(([candidate]) => candidate === code)?.[1] || code;
  };
  const shippingMapPlaceLabel = location => {
    const country = shippingMapCountryCode(location.country);
    const countryLabel = countryDisplayName(country);
    const city = String(location.city || '').trim();
    if (city) return [city, countryLabel].filter(Boolean).join(', ');
    const stateCode = country === 'US' ? shippingMapUsStateCode(location.state) : '';
    if (stateCode) return `${shippingMapUsStateName(stateCode)}, United States`;
    const state = String(location.state || '').trim();
    const rawLabel = String(location.label || '').trim();
    const startsWithState = state && rawLabel.toLowerCase().startsWith(`${state.toLowerCase()},`);
    return startsWithState ? (countryLabel || 'Shipping location') : (rawLabel || countryLabel || 'Shipping location');
  };
  const shippingMapLevelLabel = level => (
    level === 'state' ? 'state' :
    level === 'country' ? 'country' :
    level === 'place' ? 'place' :
    'shipping location'
  );
  const aggregateShippingMapLocations = (locations, zoom) => {
    const mode = shippingMapModeForZoom(zoom);
    if (mode === 'exact') {
      return (locations || []).map(location => ({
        ...location,
        label:shippingMapPlaceLabel(location),
        country:shippingMapCountryCode(location.country),
        level:'exact',
        locationCount: Number(location.locationCount || 1),
      }));
    }
    const groups = new Map();
    (locations || []).forEach(location => {
      const country = shippingMapCountryCode(location.country);
      const stateCode = country === 'US' ? shippingMapUsStateCode(location.state) : '';
      const useStateGroup = mode === 'wide' && country === 'US' && stateCode;
      const useCountryGroup = mode === 'wide' && country !== 'US' && country !== 'unknown';
      const city = String(location.city || '').trim();
      const fallbackKey = `${Number(location.latitude || 0).toFixed(4)}:${Number(location.longitude || 0).toFixed(4)}`;
      const key = useStateGroup
        ? `state:${stateCode}`
        : useCountryGroup
          ? `country:${country}`
          : `place:${country}:${(city || fallbackKey).toLowerCase()}`;
      const label = useStateGroup
        ? `${shippingMapUsStateName(stateCode)}, United States`
        : useCountryGroup
          ? countryDisplayName(country)
          : shippingMapPlaceLabel(location);
      const count = Number(location.count || 0);
      const current = groups.get(key) || {
        latitudeSum:0,
        longitudeSum:0,
        count:0,
        locationCount:0,
        label,
        city:useStateGroup || useCountryGroup ? '' : city,
        state:useStateGroup ? stateCode : '',
        country,
        level:useStateGroup ? 'state' : useCountryGroup ? 'country' : 'place',
      };
      current.latitudeSum += Number(location.latitude || 0) * count;
      current.longitudeSum += Number(location.longitude || 0) * count;
      current.count += count;
      current.locationCount += Number(location.locationCount || 1);
      groups.set(key, current);
    });
    return Array.from(groups.values())
      .filter(group => group.count > 0)
      .map(group => ({
        latitude:group.latitudeSum / group.count,
        longitude:group.longitudeSum / group.count,
        count:group.count,
        locationCount:group.locationCount,
        label:group.label,
        city:group.city,
        state:group.state,
        country:group.country,
        level:group.level,
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  };
  const createShippingMapPopup = (maps, map) => {
    const overlay = new maps.OverlayView();
    const container = document.createElement('div');
    container.style.cssText = [
      'position:absolute',
      'display:none',
      'transform:translate(-50%, calc(-100% - 14px))',
      'z-index:20',
      'pointer-events:none',
      'font-family:Inter,sans-serif',
    ].join(';');
    const card = document.createElement('div');
    card.style.cssText = [
      'display:flex',
      'align-items:center',
      'gap:10px',
      'min-width:180px',
      'max-width:260px',
      'padding:10px 12px',
      'background:#FFFFFF',
      'border:1px solid rgba(148,163,184,0.35)',
      'border-radius:12px',
      'box-shadow:0 16px 42px rgba(15,23,42,0.20)',
      'color:#0F172A',
    ].join(';');
    const flagWrap = document.createElement('div');
    flagWrap.style.cssText = [
      'width:26px',
      'height:26px',
      'border-radius:999px',
      'overflow:hidden',
      'display:flex',
      'align-items:center',
      'justify-content:center',
      'background:#EEF6FF',
      'border:1px solid #D7E7FF',
      'flex-shrink:0',
    ].join(';');
    const textWrap = document.createElement('div');
    textWrap.style.cssText = 'min-width:0;display:grid;gap:2px;';
    const title = document.createElement('div');
    title.style.cssText = 'font-size:13px;font-weight:900;line-height:1.25;color:#0F172A;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
    const meta = document.createElement('div');
    meta.style.cssText = 'font-size:11px;font-weight:700;line-height:1.3;color:#64748B;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;';
    textWrap.append(title, meta);
    const arrow = document.createElement('div');
    arrow.style.cssText = [
      'position:absolute',
      'left:50%',
      'bottom:-6px',
      'width:12px',
      'height:12px',
      'background:#fff',
      'border-right:1px solid rgba(148,163,184,0.35)',
      'border-bottom:1px solid rgba(148,163,184,0.35)',
      'transform:translateX(-50%) rotate(45deg)',
    ].join(';');
    card.append(flagWrap, textWrap);
    container.append(card, arrow);
    let position = null;
    overlay.onAdd = () => overlay.getPanes().floatPane.appendChild(container);
    overlay.draw = () => {
      if (!position) return;
      const point = overlay.getProjection().fromLatLngToDivPixel(position);
      container.style.left = `${point.x}px`;
      container.style.top = `${point.y}px`;
    };
    overlay.onRemove = () => container.remove();
    overlay.show = location => {
      position = new maps.LatLng(Number(location.latitude), Number(location.longitude));
      flagWrap.replaceChildren();
      const flagSrc = countryFlagSrc(location.country);
      if (flagSrc) {
        const img = document.createElement('img');
        img.src = flagSrc;
        img.alt = '';
        img.style.cssText = 'width:100%;height:100%;display:block;';
        flagWrap.appendChild(img);
      } else {
        const dot = document.createElement('span');
        dot.style.cssText = 'width:10px;height:10px;border-radius:999px;background:#006FDD;display:block;';
        flagWrap.appendChild(dot);
      }
      title.textContent = location.label || 'Shipping location';
      const orderText = `${Number(location.count || 0).toLocaleString()} order${Number(location.count || 0) === 1 ? '' : 's'}`;
      const locationText = Number(location.locationCount || 0) > 1
        ? ` · ${Number(location.locationCount || 0).toLocaleString()} locations`
        : '';
      meta.textContent = `${orderText}${locationText} · ${shippingMapLevelLabel(location.level)}`;
      container.style.display = 'block';
      overlay.draw();
    };
    overlay.hide = () => {
      position = null;
      container.style.display = 'none';
    };
    overlay.setMap(map);
    return overlay;
  };

  useEffect(() => {
    if (!shippingMapOpen) return;
    let active = true;
    let retryTimer = null;
    const qs = new URLSearchParams();
    qs.set('range', range);
    if (selectedProductIds.length) qs.set('product_ids', selectedProductIds.join(','));

    setShippingMapData(null);
    setShippingMapReady(false);
    setShippingMapError('');
    const load = attempt => {
      api('GET', `/api/analytics/shipping-map?${qs}`).then(({ ok, data: mapData }) => {
        if (!active) return;
        if (!ok) {
          setShippingMapError(mapData?.error || 'Shipping map could not be loaded.');
          return;
        }
        setShippingMapData(mapData);
        if (mapData.pendingCount > 0 && !mapData.geocodingError && attempt < 7) {
          retryTimer = setTimeout(() => load(attempt + 1), 1100);
        }
      });
    };
    load(0);

    return () => {
      active = false;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [shippingMapOpen, range, selectedProductIds.join(',')]);

  useEffect(() => {
    if (!shippingMapOpen || !shippingMapData || !shippingMapRef.current) return;
    if (!shippingMapData.browserApiKey) {
      setShippingMapError('Shipping map setup is incomplete. Add a dashboard Maps JavaScript API key.');
      return;
    }
    let active = true;
    const onAuthFailure = () => {
      if (active) setShippingMapError('Google Maps could not be authorized. Enable Maps JavaScript API and check the dashboard key restrictions.');
    };
    window.addEventListener(GOOGLE_MAPS_AUTH_FAILURE_EVENT, onAuthFailure);
    loadGoogleMaps(shippingMapData.browserApiKey).then(maps => {
      if (!active || !shippingMapRef.current) return;
      if (!shippingMapInstance.current) {
        shippingMapInstance.current = new maps.Map(shippingMapRef.current, {
          center:{ lat:20, lng:0 },
          zoom:2,
          gestureHandling:'greedy',
          mapTypeControl:false,
          streetViewControl:false,
          fullscreenControl:false,
        });
      }
      setShippingMapReady(true);
    }).catch(() => {
      if (active) setShippingMapError('Google Maps could not be loaded.');
    });
    return () => {
      active = false;
      window.removeEventListener(GOOGLE_MAPS_AUTH_FAILURE_EVENT, onAuthFailure);
    };
  }, [shippingMapOpen, shippingMapData?.browserApiKey]);

  useEffect(() => {
    const maps = window.google?.maps;
    const map = shippingMapInstance.current;
    if (!shippingMapOpen || !shippingMapReady || !maps || !map) return;

    shippingMapCircles.current.forEach(({ circle }) => circle.setMap(null));
    shippingMapCircles.current = [];
    if (shippingMapZoomListener.current) {
      maps.event.removeListener(shippingMapZoomListener.current);
      shippingMapZoomListener.current = null;
    }
    if (shippingMapClickListener.current) {
      maps.event.removeListener(shippingMapClickListener.current);
      shippingMapClickListener.current = null;
    }
    if (shippingMapPopupOverlay.current) {
      shippingMapPopupOverlay.current.hide();
      shippingMapPopupOverlay.current.setMap(null);
      shippingMapPopupOverlay.current = null;
    }

    const sourceLocations = shippingMapData?.locations || [];
    if (!sourceLocations.length) {
      setShippingMapDisplaySummary({ orders:0, locations:0, level:'exact' });
      return;
    }
    const popup = createShippingMapPopup(maps, map);
    shippingMapPopupOverlay.current = popup;
    const bubbleRadiusPx = (count, maxCount, level) => {
      const base = level === 'exact' ? 7 : level === 'place' ? 10 : 14;
      const spread = level === 'exact' ? 19 : level === 'place' ? 23 : 30;
      return base + spread * Math.sqrt(Number(count || 0) / Math.max(Number(maxCount || 0), 1));
    };
    const metersPerPixel = (latitude, zoom) => (
      156543.03392 * Math.cos(Number(latitude || 0) * Math.PI / 180) / Math.pow(2, zoom)
    );
    const clearCircles = () => {
      shippingMapCircles.current.forEach(({ circle }) => circle.setMap(null));
      shippingMapCircles.current = [];
    };
    const renderBubbles = () => {
      popup.hide();
      clearCircles();
      const zoom = map.getZoom() || 2;
      const locations = aggregateShippingMapLocations(sourceLocations, zoom);
      const maxCount = Math.max(...locations.map(location => Number(location.count || 0)), 1);
      const levels = new Set(locations.map(location => location.level));
      setShippingMapDisplaySummary({
        orders:locations.reduce((sum, location) => sum + Number(location.count || 0), 0),
        locations:locations.length,
        level:levels.size === 1 ? (locations[0]?.level || 'exact') : 'area',
      });
      shippingMapCircles.current = locations.map(location => {
        const center = { lat:Number(location.latitude), lng:Number(location.longitude) };
        const circle = new maps.Circle({
          map,
          center,
          radius:1000,
          strokeColor:'#006FDD',
          strokeOpacity:0.92,
          strokeWeight:1.5,
          fillColor:'#006FDD',
          fillOpacity:location.level === 'exact' ? 0.32 : location.level === 'place' ? 0.28 : 0.24,
          clickable:true,
          zIndex:Math.min(1000, Number(location.count || 0)),
        });
        circle.addListener('mouseover', () => popup.show(location));
        circle.addListener('click', () => popup.show(location));
        circle.addListener('mouseout', () => popup.hide());
        return { circle, location, radiusPx:bubbleRadiusPx(location.count, maxCount, location.level) };
      });
      shippingMapCircles.current.forEach(({ circle, location, radiusPx }) => {
        circle.setRadius(Math.max(20, radiusPx * metersPerPixel(location.latitude, zoom)));
      });
    };

    const filterKey = `${range}:${selectedProductIds.join(',')}`;
    if (shippingMapFittedFilter.current !== filterKey) {
      const bounds = new maps.LatLngBounds();
      sourceLocations.forEach(location => bounds.extend({ lat:Number(location.latitude), lng:Number(location.longitude) }));
      if (sourceLocations.length === 1) {
        map.setCenter(bounds.getCenter());
        map.setZoom(9);
        renderBubbles();
      } else {
        map.fitBounds(bounds, 48);
        maps.event.addListenerOnce(map, 'idle', () => {
          if (map.getZoom() > 10) map.setZoom(10);
          renderBubbles();
        });
      }
      shippingMapFittedFilter.current = filterKey;
    } else {
      renderBubbles();
    }
    shippingMapZoomListener.current = map.addListener('zoom_changed', renderBubbles);
    shippingMapClickListener.current = map.addListener('click', () => popup.hide());

    return () => {
      clearCircles();
      if (shippingMapZoomListener.current) {
        maps.event.removeListener(shippingMapZoomListener.current);
        shippingMapZoomListener.current = null;
      }
      if (shippingMapClickListener.current) {
        maps.event.removeListener(shippingMapClickListener.current);
        shippingMapClickListener.current = null;
      }
      if (shippingMapPopupOverlay.current) {
        shippingMapPopupOverlay.current.setMap(null);
        shippingMapPopupOverlay.current = null;
      }
    };
  }, [shippingMapOpen, shippingMapReady, shippingMapData, range, selectedProductIds.join(',')]);

  useEffect(() => {
    if (shippingMapOpen) return;
    shippingMapCircles.current.forEach(({ circle }) => circle.setMap(null));
    shippingMapCircles.current = [];
    if (shippingMapZoomListener.current && window.google?.maps) {
      window.google.maps.event.removeListener(shippingMapZoomListener.current);
      shippingMapZoomListener.current = null;
    }
    if (shippingMapClickListener.current && window.google?.maps) {
      window.google.maps.event.removeListener(shippingMapClickListener.current);
      shippingMapClickListener.current = null;
    }
    if (shippingMapPopupOverlay.current) {
      shippingMapPopupOverlay.current.setMap(null);
      shippingMapPopupOverlay.current = null;
    }
    shippingMapInstance.current = null;
    shippingMapFittedFilter.current = '';
    setShippingMapDisplaySummary({ orders:0, locations:0, level:'exact' });
  }, [shippingMapOpen]);

  const fmtMoney = (n, cur) => {
    const sym = { usd: '$', eur: '€', gbp: '£', cad: 'C$', aud: 'A$' };
    const s = sym[(cur || 'usd').toLowerCase()] || (cur || 'USD').toUpperCase() + ' ';
    return s + (n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const RANGES = [{ value:'7', label:'7d' }, { value:'30', label:'30d' }, { value:'90', label:'90d' }, { value:'all', label:'All' }];
  const GRAPH_METRICS = [{ value:'orders', label:'Orders' }, { value:'revenue', label:'Revenue' }];
  const kpis    = data?.kpis;
  const isEmpty = !loading && data && data.kpis.totalOrders === 0;
  const filterProducts = [...products].sort((a, b) => Number(b.order_count || 0) - Number(a.order_count || 0) || String(a.name || '').localeCompare(String(b.name || '')));
  const openShippingMap = () => {
    setShippingMapReady(false);
    setShippingMapError('');
    setShippingMapOpen(true);
  };
  const mappedShippingLocations = shippingMapData?.locations || [];
  const mappedShippingOrders = mappedShippingLocations.reduce((sum, location) => sum + Number(location.count || 0), 0);
  const displayedMapOrders = shippingMapDisplaySummary.orders || mappedShippingOrders;
  const displayedMapLocations = shippingMapDisplaySummary.locations || mappedShippingLocations.length;
  const displayedMapUnit = shippingMapDisplaySummary.level === 'state'
    ? 'state'
    : shippingMapDisplaySummary.level === 'country'
      ? 'country'
      : shippingMapDisplaySummary.level === 'place'
        ? 'place'
        : shippingMapDisplaySummary.level === 'area'
          ? 'area'
          : 'location';
  const selectedProductLabel = selectedProductIds.length
    ? `${selectedProductIds.length} product${selectedProductIds.length !== 1 ? 's' : ''}`
    : 'All products';
  const metricLabel = metric => metric === 'revenue' ? 'Revenue' : 'Orders';
  const metricSubtitle = metric => metric === 'revenue' ? 'Revenue totals' : 'Order count';
  const metricValue = (metric, row) => metric === 'revenue' ? Number(row?.revenue || 0) : Number(row?.count || 0);
  const timelineChartRows = fillDailyAnalytics(data?.ordersOverTime, range, null);
  const timelineHasData = !loading && timelineChartRows.some(row => metricValue(timelineMetric, row) > 0);
  const sourceHasData = !loading && (data?.bySource || []).some(row => metricValue(sourceMetric, row) > 0);
  const topProduct = kpis?.topProduct || null;
  const analyticsContainerStyle = { width:'100%', maxWidth:1640, margin:'0 auto' };
  const kpiCards = [
    { label:'Total Revenue',    value: loading ? null : fmtMoney(kpis?.totalRevenue || 0, kpis?.currency),    Icon: DollarSign, currencyRows: data?.revenueByCurrency || [] },
    { label:'Total Orders',     value: loading ? null : (kpis?.totalOrders || 0).toLocaleString(),             Icon: Package },
    {
      label:'Top Product',
      value: loading ? null : (topProduct?.name || 'No product'),
      detail: topProduct ? `${fmtMoney(topProduct.revenue || 0, topProduct.currency)} · ${topProduct.orderCount || 0} order${topProduct.orderCount === 1 ? '' : 's'}` : '',
      imageSrc: topProduct?.imageData || '',
      Icon: Package,
    },
    { label:'Avg. Order Value', value: loading ? null : fmtMoney(kpis?.avgOrderValue || 0, kpis?.currency),    Icon: BarChart2 },
  ];

  const renderMetricToggle = (value, onChange) => (
    <div style={{ display:'flex', background:'#F3F4F6', borderRadius:9, padding:3, gap:2, flexShrink:0 }} aria-label="Analytics metric">
      {GRAPH_METRICS.map(metric => (
        <button key={metric.value} onClick={() => onChange(metric.value)} style={{
          background: value === metric.value ? '#fff' : 'transparent', border:'none', borderRadius:7,
          padding:'6px 12px', fontSize:13, fontWeight: value === metric.value ? 700 : 500,
          color: value === metric.value ? '#1A1A2E' : '#6B7280', cursor:'pointer',
          boxShadow: value === metric.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition:'all 0.12s',
        }}>{metric.label}</button>
      ))}
    </div>
  );

  const chartCard = (title, sub, ref, hasData, emptyMsg = 'No data', action = null, chartHeight = 280) => (
    <div style={{ background:'#fff', border:'1px solid #DDE3EA', borderRadius:10, padding:'20px 22px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <div>
          <div style={{ fontSize:13, fontWeight:700, color:'#111827', marginBottom:2 }}>{title}</div>
          <div style={{ fontSize:12, color:'#9CA3AF' }}>{sub}</div>
        </div>
        {action}
      </div>
      {loading
        ? <div style={{ height:chartHeight, borderRadius:8, background:'#F9FAFB' }}/>
        : hasData
          ? chartError
            ? <div style={{ height:chartHeight, display:'flex', alignItems:'center', justifyContent:'center', color:'#B91C1C', fontSize:13 }}>{chartError}</div>
            : !chartReady
              ? <div style={{ height:chartHeight, display:'flex', alignItems:'center', justifyContent:'center', color:'#9CA3AF', fontSize:13 }}>Loading chart...</div>
              : <div style={{ height:chartHeight, position:'relative' }}><canvas ref={ref}/></div>
          : <div style={{ height:chartHeight, display:'flex', alignItems:'center', justifyContent:'center', color:'#9CA3AF', fontSize:13 }}>{emptyMsg}</div>
      }
    </div>
  );
  const countriesCard = () => {
    const metricIsRevenue = countryMetric === 'revenue';
    const countryMetricValue = row => metricValue(countryMetric, row);
    const countryMetricLabel = value => metricIsRevenue ? fmtMoney(value, kpis?.currency) : value.toLocaleString();
    const rows = [...(data?.byCountry || [])].sort((a, b) => countryMetricValue(b) - countryMetricValue(a));
    const max = Math.max(...rows.map(countryMetricValue), 1);
    return (
      <div style={{ background:'#fff', border:'1px solid #DDE3EA', borderRadius:10, padding:'20px 22px' }}>
        <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12, marginBottom:14, flexWrap:'wrap' }}>
          <div>
            <div style={{ fontSize:13, fontWeight:700, color:'#111827', marginBottom:2 }}>Top countries</div>
            <div style={{ fontSize:12, color:'#9CA3AF' }}>{metricIsRevenue ? `Revenue by country in ${(kpis?.currency || 'usd').toUpperCase()}` : 'Order volume by country'}</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', justifyContent:'flex-end' }}>
            {renderMetricToggle(countryMetric, setCountryMetric)}
            <button
              type="button"
              onClick={openShippingMap}
              disabled={loading || Number(kpis?.totalOrders || 0) === 0}
              style={{
                height:34, display:'inline-flex', alignItems:'center', gap:6, border:'1px solid #DDE3EA',
                background:'#fff', color: loading || Number(kpis?.totalOrders || 0) === 0 ? '#9CA3AF' : '#111827',
                borderRadius:7, padding:'0 9px', fontSize:12, fontWeight:700,
                cursor: loading || Number(kpis?.totalOrders || 0) === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              <MapIcon size={14} strokeWidth={2}/>
              <span>View map</span>
            </button>
          </div>
        </div>
        {loading
          ? <div style={{ height:280, borderRadius:8, background:'#F9FAFB' }}/>
          : rows.length > 0
            ? (
              <div style={{ height:280, overflow:'auto', paddingRight:4 }}>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:'9px 12px' }}>
                  {rows.map(c => {
                    const value = countryMetricValue(c);
                    const pct = Math.max(5, Math.round((value / max) * 100));
                    return (
                      <div key={c.country || 'Unknown'} title={`${countryName(c.country)} · ${metricIsRevenue ? countryMetricLabel(value) : `${c.count} order${c.count !== 1 ? 's' : ''}`}`} style={{ minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:4 }}>
                          <div style={{ width:22, height:22, borderRadius:'50%', background:'#F3F4F6', border:'1px solid #E5E7EB', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:800, color:'#9CA3AF', lineHeight:1, flexShrink:0, overflow:'hidden' }}>
                            {countryFlagSrc(c.country)
                              ? <img src={countryFlagSrc(c.country)} alt="" style={{ width:'100%', height:'100%', display:'block' }}/>
                              : '?'}
                          </div>
                          <div style={{ minWidth:0, flex:1, display:'flex', alignItems:'baseline', justifyContent:'space-between', gap:6 }}>
                            <span style={{ fontSize:12, fontWeight:700, color:'#374151', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{countryName(c.country)}</span>
                            <span style={{ fontSize:11, fontWeight:800, color:'#6B7280', fontVariantNumeric:'tabular-nums' }}>{countryMetricLabel(value)}</span>
                          </div>
                        </div>
                        <div style={{ height:3, background:'#F3F4F6', borderRadius:999, overflow:'hidden' }}>
                          <div style={{ width:`${pct}%`, height:'100%', background:'#006FDD', borderRadius:999 }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )
            : <div style={{ height:280, display:'flex', alignItems:'center', justifyContent:'center', color:'#9CA3AF', fontSize:13 }}>No country data</div>
        }
      </div>
    );
  };

  return (
    <div className="dash-page-scroll workspace-page" style={{ padding:'0 0 36px', maxWidth:'none', margin:0 }}>
      <div style={{ marginBottom:14, overflow:'visible', position:'relative', zIndex: productFilterOpen ? 60 : 40, background:'radial-gradient(circle at 12% 0%, rgba(0, 111, 221, 0.08), transparent 30%), radial-gradient(circle at 88% 0%, rgba(115, 208, 75, 0.10), transparent 26%), linear-gradient(180deg, #FFFFFF 0%, #F8FBFF 100%)', borderBottom:'1px solid rgba(199, 213, 230, 0.78)' }}>
      <div style={analyticsContainerStyle}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:18, flexWrap:'wrap', padding:'16px 24px' }}>
        <div>
          <h1 className="dash-page-title">Analytics</h1>
          <p className="dash-page-subtitle">Order volume, revenue, and routing readiness.</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:10, flexWrap:'wrap' }}>
          {filterProducts.length > 0 && (
            <div ref={productFilterRef} style={{ position:'relative' }}>
              <button
                onClick={() => setProductFilterOpen(open => !open)}
                style={{
                  height:34, display:'inline-flex', alignItems:'center', gap:7, border:'1px solid #E5E7EB',
                  background:'#fff', color:'#374151', borderRadius:8, padding:'0 10px', fontSize:13,
                  fontWeight:700, cursor:'pointer', boxShadow: productFilterOpen ? '0 3px 10px rgba(17,24,39,0.08)' : 'none',
                  maxWidth:220,
                }}
                aria-haspopup="menu"
                aria-expanded={productFilterOpen}
              >
                <SlidersHorizontal size={15} strokeWidth={2} color="#111827"/>
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>Filter: {selectedProductLabel}</span>
                <ChevronDown size={14} strokeWidth={2.2} style={{ color:'#9CA3AF', flexShrink:0 }}/>
              </button>
              {productFilterOpen && (
                <div style={{
                  position:'absolute', right:0, top:'calc(100% + 8px)', width:310, maxWidth:'calc(100vw - 40px)',
                  background:'#fff', border:'1px solid #E5E7EB', borderRadius:10, boxShadow:'0 18px 45px rgba(17,24,39,0.14)',
                  zIndex:1000, padding:8,
                }}>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, padding:'4px 4px 8px' }}>
                    <div>
                      <div style={{ fontSize:13, fontWeight:800, color:'#1A1A2E' }}>Products</div>
                      <div style={{ fontSize:12, color:'#9CA3AF', marginTop:2 }}>{selectedProductIds.length ? `${selectedProductIds.length} selected` : 'All included'}</div>
                    </div>
                    {selectedProductIds.length > 0 && (
                      <button onClick={() => setSelectedProductIds([])} style={{ border:'1px solid #E5E7EB', background:'#F9FAFB', color:'#6B7280', borderRadius:7, padding:'5px 9px', fontSize:12, fontWeight:700, cursor:'pointer' }}>Clear</button>
                    )}
                  </div>
                  <button onClick={() => setSelectedProductIds([])} style={{
                    width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
                    border:'none', background: selectedProductIds.length ? '#fff' : '#EEF6FF', color: selectedProductIds.length ? '#374151' : '#0F4F99',
                    borderRadius:8, padding:'9px 10px', fontSize:13, fontWeight:700, cursor:'pointer', textAlign:'left',
                  }}>
                    <span>All products</span>
                    {!selectedProductIds.length && <Check size={15} strokeWidth={2.4}/>}
                  </button>
                  <div style={{ maxHeight:268, overflow:'auto', marginTop:4 }}>
                    {filterProducts.map(p => {
                      const active = selectedProductIds.includes(p.id);
                      return (
                        <button key={p.id} onClick={() => toggleProduct(p.id)} style={{
                          width:'100%', display:'flex', alignItems:'center', gap:9, border:'none',
                          background: active ? '#EEF6FF' : '#fff', color: active ? '#0F4F99' : '#374151',
                          borderRadius:8, padding:'8px 10px', fontSize:13, fontWeight:700, cursor:'pointer', textAlign:'left',
                        }}>
                          <ProductThumb src={p.image_data} size={24}/>
                          <span style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.name}</span>
                          <span style={{ color: active ? '#006FDD' : '#9CA3AF', fontSize:12, fontVariantNumeric:'tabular-nums' }}>{p.order_count || 0}</span>
                          {active && <Check size={15} strokeWidth={2.4} style={{ flexShrink:0 }}/>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
          <div style={{ display:'flex', alignItems:'center', gap:4, flexWrap:'wrap' }}>
            {RANGES.map(r => (
              <button key={r.value} onClick={() => handleRangeClick(r.value)} style={{
                background: range === r.value ? '#fff' : 'transparent', border: range === r.value ? '1px solid #E5E7EB' : '1px solid transparent', borderRadius:7,
                padding:'6px 14px', fontSize:13, fontWeight: range === r.value ? 600 : 400,
                color: range === r.value ? '#1A1A2E' : '#6B7280', cursor:'pointer',
                boxShadow: range === r.value ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition:'all 0.12s',
              }}>{r.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding:'16px 24px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(0, 1fr))', gap:8 }}>
          {kpiCards.map(({ label, value, detail, imageSrc, Icon, currencyRows }) => (
            <div key={label} style={{ padding:'4px 12px', borderRight: label === 'Avg. Order Value' ? 'none' : '1px solid #F3F4F6', minWidth:0 }}>
              <div style={{ fontSize:11, color:'#9CA3AF', fontWeight:800, letterSpacing:'0.04em', textTransform:'uppercase', marginBottom:10 }}>{label}</div>
              <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                <div style={{ width:22, height:22, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  {imageSrc
                    ? <ProductThumb src={imageSrc} size={22}/>
                    : <Icon size={18} strokeWidth={2} color="#111827"/>}
                </div>
                {loading
                  ? <div style={{ height:24, borderRadius:6, background:'#F3F4F6', width:'70%' }}/>
                  : <div style={{ display:'flex', alignItems:'baseline', gap:7, minWidth:0 }}>
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontSize: label === 'Top Product' ? 15 : 21, lineHeight: label === 'Top Product' ? 1.2 : 1.1, fontWeight:800, letterSpacing:0, color:'#1A1A2E', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{value}</div>
                        {detail && <div style={{ fontSize:11, fontWeight:700, color:'#9CA3AF', marginTop:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{detail}</div>}
                      </div>
                      <CurrencyBreakdownBadge rows={currencyRows || []}/>
                    </div>
                }
              </div>
            </div>
          ))}
        </div>
      </div>
      </div>
      </div>

      <div style={{ ...analyticsContainerStyle, padding:'0 24px' }}>
      {isEmpty ? (
        <div style={{ background:'#fff', border:'1px solid #DDE3EA', borderRadius:10, padding:'60px 40px', textAlign:'center' }}>
          <div style={{ width:24, height:24, display:'inline-flex', alignItems:'center', justifyContent:'center', marginBottom:12 }}>
            <Inbox size={20} strokeWidth={2} color="#111827"/>
          </div>
          <div style={{ fontSize:15, fontWeight:600, color:'#6B7280', marginBottom:6 }}>No orders in this period</div>
          <div style={{ fontSize:13, color:'#9CA3AF' }}>Try selecting a wider time range or import some orders first.</div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom:14 }}>
            {chartCard(`${metricLabel(timelineMetric)} over time`, timelineMetric === 'revenue' ? `Daily revenue in ${(kpis?.currency || 'usd').toUpperCase()}` : 'Daily order count', timelineRef, timelineHasData, `No ${metricLabel(timelineMetric).toLowerCase()} data`, renderMetricToggle(timelineMetric, setTimelineMetric), 320)}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 520px), 1fr))', gap:14, paddingBottom:36 }}>
            {chartCard(`${metricLabel(sourceMetric)} by source`, `${metricSubtitle(sourceMetric)} by platform`, sourceRef, sourceHasData, `No ${metricLabel(sourceMetric).toLowerCase()} data`, renderMetricToggle(sourceMetric, setSourceMetric))}
            {countriesCard()}
          </div>
        </>
      )}
      </div>
      {shippingMapOpen && (
        <div
          onClick={() => setShippingMapOpen(false)}
          style={{ position:'fixed', inset:0, background:'rgba(17,24,39,0.38)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:18 }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Shipping destination map"
            onClick={e => e.stopPropagation()}
            style={{ width:'min(1180px, calc(100vw - 36px))', maxHeight:'min(860px, calc(100vh - 36px))', background:'#fff', border:'1px solid #DDE3EA', borderRadius:12, boxShadow:'0 24px 80px rgba(15,23,42,0.24)', overflow:'hidden', display:'flex', flexDirection:'column' }}
          >
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:14, padding:'18px 20px', borderBottom:'1px solid #E5E7EB' }}>
              <div>
                <div style={{ fontSize:15, fontWeight:800, color:'#111827' }}>Shipping destinations</div>
                <div style={{ fontSize:12, color:'#6B7280', marginTop:3 }}>
                  Zoomed out, US orders group by state and other destinations group by country.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShippingMapOpen(false)}
                style={{ border:'1px solid #DDE3EA', background:'#fff', color:'#111827', borderRadius:7, padding:'7px 10px', fontSize:12, fontWeight:800, cursor:'pointer' }}
              >
                Close
              </button>
            </div>
            <div style={{ padding:20, overflow:'auto' }}>
              <div style={{ height:'min(540px, calc(100vh - 260px))', minWidth:760, overflow:'hidden', position:'relative', background:'#fff' }}>
                {!shippingMapReady && !shippingMapError && (
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#9CA3AF', fontSize:13, zIndex:1 }}>Loading map...</div>
                )}
                {shippingMapReady && mappedShippingLocations.length === 0 && !shippingMapError && (
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#9CA3AF', fontSize:13, zIndex:1 }}>
                    {shippingMapData?.pendingCount ? 'Resolving shipping locations...' : 'No shipping addresses are available for this period.'}
                  </div>
                )}
                {shippingMapError && (
                  <div style={{ position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#9CA3AF', fontSize:13, zIndex:2 }}>{shippingMapError}</div>
                )}
                <div ref={shippingMapRef} style={{ width:'100%', height:'100%', opacity: shippingMapReady && !shippingMapError ? 1 : 0 }}/>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr auto', gap:18, marginTop:16, alignItems:'center' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                  <span style={{ width:10, height:10, borderRadius:'50%', background:'rgba(0,111,221,0.34)', border:'1px solid #006FDD', flexShrink:0 }}/>
                  <span style={{ width:19, height:19, borderRadius:'50%', background:'rgba(0,111,221,0.34)', border:'1px solid #006FDD', flexShrink:0 }}/>
                  <span style={{ fontSize:12, color:'#6B7280' }}>Circle size reflects order count. Zoom in to split groups into places.</span>
                </div>
                <div style={{ fontSize:12, color:'#6B7280', textAlign:'right' }}>
                  {displayedMapOrders.toLocaleString()} mapped order{displayedMapOrders === 1 ? '' : 's'} across {displayedMapLocations.toLocaleString()} {displayedMapUnit}{displayedMapLocations === 1 ? '' : 's'}
                </div>
              </div>
              {(shippingMapData?.pendingCount > 0 || shippingMapData?.failedCount > 0 || shippingMapData?.truncated || shippingMapData?.geocodingError) && (
                <div style={{ marginTop:10, fontSize:12, color:'#9CA3AF', lineHeight:1.5 }}>
                  {shippingMapData?.pendingCount > 0 && !shippingMapData?.geocodingError ? `Resolving ${shippingMapData.pendingCount.toLocaleString()} more shipping location${shippingMapData.pendingCount === 1 ? '' : 's'}... ` : ''}
                  {shippingMapData?.failedCount > 0 ? `${shippingMapData.failedCount.toLocaleString()} address${shippingMapData.failedCount === 1 ? '' : 'es'} could not be mapped. ` : ''}
                  {shippingMapData?.truncated ? 'Showing the highest-volume shipping locations. ' : ''}
                  {shippingMapData?.geocodingError ? 'Some shipping locations could not be resolved right now.' : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
